import React, { useState, useEffect } from 'react';
import Barra from '../../components/barrasuperior';
import { renovarPrestamo, obtenerPrestamosPorEstudiantePaginados } from '../../services/PrestamosService';
import { obtenerMultaPorPrestamo } from '../../services/MultaService';
import { acortarTexto, formatearFecha, getColorPorEstado, jwtDecoder, useGetWindowDimensions } from '../../utils/functions';
import Footer from '../../components/homecomponentes/footer';
import Swal from 'sweetalert2';
import './misPrestamos.css';
import Loader from '../../components/loader';
import LittleLoader from '../../components/littleLoader/littleLoader';
import { useUser } from '../../context/userContext';
import { etiquetaCopia } from '../../utils/copiasPrestamo';
import BookImage from '../../components/bookImage';

function MisPrestamos() {
  const { userData } = useUser();
  const [prestamosData, setPrestamosData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listEnded, setListEnded] = useState(false);
  const [nextPage, setNextPage] = useState(0);
  const pageSize = 10; // Tamaño de página

  // Resetear el estado cuando el usuario cambia
  useEffect(() => {
    if (userData.cif) {
      setPrestamosData([]);
      setListEnded(false);
      setNextPage(0);
    }
  }, [userData.cif]);

  // Fetch de datos cuando `nextPage` cambia
  useEffect(() => {
    const fetchPrestamosData = async () => {
      if (userData.cif && !listEnded) {
        setLoading(true);
        try {
          const prestamosResponse = await obtenerPrestamosPorEstudiantePaginados(userData.cif, nextPage, pageSize);
          setPrestamosData((prevPrestamos) => {
            return [...prevPrestamos, ...prestamosResponse.content];
          });
          if (prestamosResponse.last) {
            setListEnded(true);
          }
        } catch (error) {
          Swal.fire({
            title: 'Error obteniendo tus préstamos',
            text: error.message
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPrestamosData();
  }, [nextPage, userData.cif]);

  // Manejo del scroll para el infinite scroll
  const handleScroll = () => {
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 500 && !listEnded && !loading) {
      setNextPage((prev) => prev + 1);
    }
  };

  // Agregar y limpiar el event listener del scroll
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [listEnded, loading]);

  // Mostrar el loader inicial
  if (prestamosData.length === 0 && loading) {
    return (
      <Loader label={'Cargando ...'} />
    );
  } else {
    return (
      <div>
        <Barra />
        <div className='misPrestamos-container' style={{ marginBottom: '20rem'}}>
          <div className='franja'>
            <h1 className='franja-titulo'>Mis Préstamos</h1>
          </div>
          <div className='prestamosList-container'>
            {
              prestamosData.length > 0 ? (
                <div className='misPrestamosGrid-container'>
                  {prestamosData.map((prestamo) => (
                    <MisPrestamosItem prestamo={prestamo} userId={userData.cif} key={prestamo.idPrestamo} />
                  ))}
                </div>
              ) : (
                <div className='misPrestamos-vacio'>
                  <h1>¡Aún no has prestado ningún libro!</h1>
                </div>
              )
            }
          </div>
          {loading && <LittleLoader />}
        </div>
        <Footer />
      </div>
    );
  }
}


function MisPrestamosItem({ userId, prestamo }) {
  const handleRenovarClick = async () => {
    try {
      const respuesta = await renovarPrestamo(userId, prestamo.idPrestamo);
      Swal.fire({
        title: 'Renovación Exitosa',
        text: `Tu préstamo ha sido renovado correctamente hasta el ${formatearFecha(respuesta.fechaLimiteDevolucion)}`,
        icon: 'success',
        confirmButtonColor: '#21807C'
      });
    } catch (error) {
      const mensajeError = error.response && error.response.data && typeof error.response.data === 'string'
        ? error.response.data
        : 'No se pudo renovar tu préstamo debido a un error inesperado.';
      Swal.fire({
        title: 'Error al Renovar',
        text: mensajeError,
        icon: 'error',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleVerDetallesMultaClick = async () => {
    try {
      const multa = await obtenerMultaPorPrestamo(prestamo.idPrestamo);
      Swal.fire({
        title: 'Detalles de la Multa',
        html: `
               <strong>Libro Prestado:</strong> ${multa.prestamo.libro.titulo}
               <br>
               <strong>Debió haberlo devuelto el:</strong> ${formatearFecha(multa.prestamo.fechaLimiteDevolucion)}
               <br>
               <strong>Monto a pagar:</strong> $ ${multa.montoMulta} ${multa.montoMulta > 1 ? `dólares` : `dólar`} 
               <div style="margin-top: 40px;">
                  Para realizar el pago de la multa debe presentarse en la biblioteca de la universidad
               </div>
               `,
        icon: 'info',
        confirmButtonColor: '#ce2a2a',
        customClass: {
          title: 'custom-title',
          icon: 'custom-title'
        }
      });
    } catch (error) {
      const mensajeError = error.response && error.response.data && typeof error.response.data === 'string'
        ? error.response.data : 'No se pudo obtener los detalles de la multa debido a un error inesperado.'
      Swal.fire({
        title: 'Error al Ver Detalles de Multa',
        text: mensajeError,
        icon: 'error',
        confirmButtonColor: '#d33'
      });
    }
  };

  return (
    <div className='misPrestamosItem-container'>
      <BookImage
        src={prestamo.libroImagen}
        alt={prestamo.libroTitulo}
        className={"misPrestamosItem-imagen"}
      />
      <div className='misPrestamosItem-info'>
        <h2>{acortarTexto(prestamo.libroTitulo,38)}</h2>
        <p className='prestamoDropDownItem-subtitle'>Fecha límite de devolución:</p>
        <p className='prestamoDropDownItem-subtitle2'>{formatearFecha(prestamo.fechaLimiteDevolucion)}</p>
        <p className='prestamoDropDownItem-subtitle'>Código de Retiro: </p>
        <p className='prestamoDropDownItem-subtitle2'>{prestamo.codigoRetiro}</p>
        <p className='prestamoDropDownItem-subtitle'>{prestamo.estado === 'aprobado' ? 'Copia reservada:' : 'Copia:'}</p>
        <p className='prestamoDropDownItem-subtitle2'>{etiquetaCopia(prestamo)}</p>
        <p className='prestamoDropDownItem-subtitle'>Estado: </p>
        <p className='prestamoDropDownItem-subtitle3' style={{ color: getColorPorEstado(prestamo.estado) }}>{prestamo.estado}</p>
        {prestamo.estado === 'en ejecución' && (
          <button onClick={handleRenovarClick} className='misPrestamosItem-btn'>Renovar</button>
        )}
        {
          prestamo.estado === 'multado' && (
            <button onClick={handleVerDetallesMultaClick} className='misPrestamosItem-btnMultado'>Ver Multa</button>
          )
        }
      </div>
    </div>
  );
}

export default MisPrestamos;

